import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  TASK_TYPE_LABELS_FR,
  formatTaskRef,
  taskStatusLabelFr,
  type Task,
} from "@relais/shared";
import { STATUS_ACCENT } from "@relais/design-system";
import { useOffline } from "../../lib/offline-context.js";
import { useSession } from "../../lib/auth.js";
import { ArchivedRenderBody } from "../../features/task-detail/archive/ArchivedRenderBody.js";
import { ServerPdfButton } from "../../features/task-detail/archive/ServerPdfButton.js";
import { useArchivedRender } from "../../features/task-detail/archive/use-archived-render.js";

export const Route = createFileRoute("/tasks/$taskId_/rapport")({
  component: RapportPage,
});

/**
 * Consultation d'un compte rendu déjà transmis — demandé par David le
 * 02/10/2026 : « est-ce qu'on peut revenir voir le rapport après l'avoir
 * validé ? ». Jusqu'ici le relais butait sur « rendu verrouillé » : exact sur
 * le fond (rien n'est modifiable), mais il n'avait aucun autre chemin pour
 * relire son propre travail.
 *
 * Écran à part, et pas un assouplissement du formulaire : le verrou d'édition
 * reste entier, et la lecture ne passe jamais par les mêmes routes.
 */
function RapportPage() {
  const { taskId } = Route.useParams();
  const { service, tick } = useOffline();
  const session = useSession();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [missing, setMissing] = useState(false);
  const render = useArchivedRender(taskId);

  useEffect(() => {
    if (!service) return;
    let cancelled = false;
    void service.getTask(taskId).then((t) => {
      if (cancelled) return;
      if (t) setTask(t);
      else setMissing(true);
    });
    return () => {
      cancelled = true;
    };
  }, [service, taskId, tick]);

  if (missing) {
    return (
      <Shell onClose={() => navigate({ to: "/" })}>
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Mission introuvable.
        </p>
      </Shell>
    );
  }
  if (!task || session.status !== "authed") {
    return (
      <Shell onClose={() => navigate({ to: "/" })}>
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Chargement…
        </p>
      </Shell>
    );
  }

  const close = () =>
    navigate({ to: "/tasks/$taskId", params: { taskId } });

  // Avant la soumission il n'y a pas de compte rendu à relire, seulement un
  // formulaire en cours — qui a son propre écran, modifiable.
  if (!task.submitted_at) {
    return (
      <Shell onClose={close}>
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Cette mission n'a pas encore été transmise.
        </p>
      </Shell>
    );
  }

  return (
    <Shell onClose={close} task={task}>
      <ServerPdfButton task={task} />

      {render.status === "loading" ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Chargement du compte rendu…
        </p>
      ) : render.status === "empty" ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Aucun compte rendu à afficher pour cette mission.
        </p>
      ) : (
        <>
          {/* Hors-ligne, ce qui s'affiche est la dernière copie de l'appareil :
              elle peut ignorer les corrections faites à la relecture. On le dit
              plutôt que de laisser croire à la version définitive. */}
          {render.source === "local" ? (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Copie locale — les corrections éventuelles de votre référent ne
              s'affichent qu'une fois reconnecté.
            </p>
          ) : null}
          <ArchivedRenderBody
            task={task}
            userId={session.session.user.id}
            payload={render.payload}
            medias={render.medias}
          />
        </>
      )}
    </Shell>
  );
}

function Shell({
  task,
  onClose,
  children,
}: {
  task?: Task;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-16">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {task ? (
            <span
              className={
                "inline-block rounded-[15px] px-3 py-1 text-xs font-normal " +
                STATUS_ACCENT[task.status].badge
              }
            >
              {taskStatusLabelFr(task)}
            </span>
          ) : null}
          <h2 className="mt-3 text-[22px] font-medium leading-tight text-[#06070C]">
            {task ? `Compte rendu · ${TASK_TYPE_LABELS_FR[task.type]}` : "Compte rendu"}
          </h2>
          {task ? (
            <p className="mt-1 font-mono text-xs tracking-wide text-[#616269]">
              {formatTaskRef(task.id)}
            </p>
          ) : null}
        </div>
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="grid size-10 shrink-0 place-items-center rounded-[10px] border border-[#919191] text-[#06070C] active:scale-95"
        >
          <X className="size-5" aria-hidden />
        </button>
      </header>
      {children}
    </div>
  );
}
